"use client";

/**
 * `<GaltonBoard>` — one dot per simulation agent falls through a peg
 * lattice into BULL / BASE / BEAR bins; once every dot has settled, a
 * consensus card appears. The distribution IS the verdict.
 *
 * Reimplemented from `docs/Fable UiUx review/wtm-components-telemetry-
 * galton-anchor.html` (section 2) on the committed motion system, FIXING
 * the prototype's confirmed Phase-7 defect — see `./galton-math.ts` for
 * the structural fix (exact-frame counter sync, bin-clamped final
 * waypoints, exact configured totals, landed-only split percentages).
 *
 * Architecture (Global rule 5): everything visual is a pure function of
 * the `useStageDriver` progress — stage 0 is the drop, stage 1 is the
 * verdict hold. Per-frame ball positions and counters are painted
 * imperatively via the driver's `onFrame` escape hatch (80 SVG circles at
 * 60fps would be wasteful as React state); coarse UI (stage badges, the
 * consensus card) re-renders at 8fps. Hover pauses, off-viewport pauses,
 * and looping all come from the driver.
 *
 * Reduced motion + pre-hydration: renders the SETTLED end state — full
 * bins, final counts, consensus card — statically.
 *
 * Usage (the /simulate page-wiring batch):
 *   <GaltonBoard distribution={{ bull: 24, base: 36, bear: 20 }} ticker="MSFT" />
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useStageDriver } from "@/lib/motion";
import { EASE, LINE, LINE_STRONG, MONO, SERIF } from "./shared";
import {
  GALTON_BIN_LABELS,
  GALTON_LAYOUT,
  buildGaltonRun,
  landedCountsAt,
  leadingBin,
  positionAt,
  releasedCountAt,
  splitFromCounts,
  type GaltonDistribution,
} from "./galton-math";

export interface GaltonBoardProps {
  /** Final landed counts per bin, e.g. `{ bull: 24, base: 36, bear: 20 }`. */
  distribution: GaltonDistribution;
  /** Optional ticker badge, e.g. "MSFT". */
  ticker?: string;
  /** Seed for the deterministic ball paths (SSR + client must agree). */
  seed?: number;
  className?: string;
}

/** Stage durations (ms): [drop, verdict hold]. Loops via the driver. */
const DURATIONS = [9500, 5500];

const BIN_FILL = [
  "var(--color-green, #10B981)",
  "var(--color-blue, #3B82F6)",
  "var(--color-red, #EF4444)",
] as const;
const FALL_FILL = "var(--color-gold, #F59E0B)";

const CSS = `
.wtm-glt-grid{display:grid;grid-template-columns:1fr 220px}
.wtm-glt-side{border-left:1px solid ${LINE}}
@media (max-width:760px){
  .wtm-glt-grid{grid-template-columns:1fr}
  .wtm-glt-side{border-left:none;border-top:1px solid ${LINE}}
}
@keyframes wtm-glt-pulse{0%,100%{opacity:1}50%{opacity:.35}}
@media (prefers-reduced-motion:reduce){.wtm-glt-pulse-dot{animation:none!important}}
`;

const labelStyle: CSSProperties = {
  fontFamily: MONO,
  fontSize: 10,
  letterSpacing: "0.14em",
  color: "var(--color-dim, #7E8FA6)",
  textTransform: "uppercase",
};

/** Drop-stage fall progress for a driver frame (1 once past stage 0). */
function fallProgressOf(frame: { stage: number; stageProgress: number }): number {
  return frame.stage === 0 ? frame.stageProgress : 1;
}

export function GaltonBoard({ distribution, ticker, seed = 7, className }: GaltonBoardProps) {
  const run = useMemo(
    () => buildGaltonRun(distribution, { seed }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- field-level deps avoid object-identity re-runs
    [distribution.bull, distribution.base, distribution.bear, seed]
  );

  // Pre-hydration renders the settled end state; the drop starts on mount.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // --- imperative paint targets (onFrame escape hatch)
  const dotRefs = useRef<Array<SVGCircleElement | null>>([]);
  const countRefs = useRef<Array<SVGTextElement | null>>([]);
  const droppedRef = useRef<HTMLDivElement | null>(null);
  const splitRef = useRef<HTMLDivElement | null>(null);
  const animatedRef = useRef(false);
  const runRef = useRef(run);
  runRef.current = run;

  const driver = useStageDriver<HTMLDivElement>({
    durations: DURATIONS,
    reactStateFps: 8, // coarse UI only — balls/counters paint via onFrame
    onFrame: (frame) => {
      if (!animatedRef.current) return;
      const r = runRef.current;
      const t = fallProgressOf(frame);
      for (let i = 0; i < r.balls.length; i++) {
        const el = dotRefs.current[i];
        if (!el) continue;
        const ball = r.balls[i];
        const pos = positionAt(ball, t);
        el.setAttribute("cx", String(pos.x));
        el.setAttribute("cy", String(pos.y));
        el.setAttribute("opacity", pos.released ? "1" : "0");
        // Bin color from the EXACT settle frame — the same frame the
        // counter below includes this ball (defect fix a).
        el.setAttribute("fill", pos.landed ? BIN_FILL[ball.bin] : FALL_FILL);
      }
      const counts = landedCountsAt(r.balls, t);
      for (let b = 0; b < 3; b++) {
        const el = countRefs.current[b];
        if (el) el.textContent = String(counts[b]);
      }
      if (droppedRef.current) {
        droppedRef.current.textContent = `${releasedCountAt(r.balls, t)} / ${r.total}`;
      }
      if (splitRef.current) {
        const split = splitFromCounts(counts); // landed only (defect fix d)
        splitRef.current.textContent = split
          ? split.map((p) => `${p}%`).join(" / ")
          : "- / - / -";
      }
    },
  });

  const animated = mounted && !driver.reducedMotion;
  animatedRef.current = animated;

  const L = GALTON_LAYOUT;
  const finalCounts = run.target;
  const finalSplit = splitFromCounts(finalCounts) ?? [0, 0, 0];
  const lead = leadingBin(finalCounts);
  const verdictText = `${run.total} AGENTS · ${GALTON_BIN_LABELS[lead]} CASE LEADS · ${finalSplit[lead]}%`;
  const consensusVisible = !animated || driver.frame.stage >= 1;

  const pegs = useMemo(() => {
    const out: Array<{ x: number; y: number }> = [];
    for (let r = 0; r < L.pegRows; r++) {
      const n = L.pegBaseCount + r;
      for (let i = 0; i < n; i++) {
        out.push({ x: L.dropX + (i - (n - 1) / 2) * L.pegSpacing, y: L.pegTop + r * L.pegRowHeight });
      }
    }
    return out;
  }, [L]);

  const scenarioRows = [0, 1, 2].map((b) => ({
    label: GALTON_BIN_LABELS[b],
    pct: finalSplit[b],
    fill: BIN_FILL[b],
  }));

  return (
    <section
      ref={driver.ref}
      className={className}
      aria-label={`Scenario distribution: ${run.total} AI agents: ${finalSplit[0]}% bull, ${finalSplit[1]}% base, ${finalSplit[2]}% bear. Experimental AI simulation, illustrative data.`}
      style={{
        background: "var(--color-bg, #0A0E1A)",
        border: `1px solid ${LINE}`,
        borderRadius: 14,
        overflow: "hidden",
        boxShadow: "0 24px 60px -24px rgba(0,0,0,.55)",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      <style>{CSS}</style>

      {/* header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
          padding: "12px 20px",
          borderBottom: `1px solid ${LINE}`,
          fontFamily: MONO,
          fontSize: 11,
          letterSpacing: "0.1em",
          color: "var(--color-dim, #7E8FA6)",
        }}
      >
        <span
          style={{
            border: "1px solid rgba(245,158,11,.5)",
            color: "var(--color-gold, #F59E0B)",
            borderRadius: 99,
            padding: "3px 11px",
          }}
        >
          WTM SIMULATE
        </span>
        <span style={{ border: `1px solid ${LINE_STRONG}`, borderRadius: 99, padding: "3px 11px" }}>
          SCENARIO DISTRIBUTION · {run.total} AGENTS
        </span>
        {ticker ? (
          <span style={{ border: `1px solid ${LINE_STRONG}`, borderRadius: 99, padding: "3px 11px" }}>
            TICKER · {ticker}
          </span>
        ) : null}
        <span
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            color: "var(--color-green, #10B981)",
          }}
        >
          <span
            className="wtm-glt-pulse-dot"
            aria-hidden
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: "var(--color-green, #10B981)",
              animation: `wtm-glt-pulse 1.6s ${EASE} infinite`,
            }}
          />
          {consensusVisible ? "VERDICT" : "DROPPING"}
        </span>
      </div>

      <div className="wtm-glt-grid">
        {/* board */}
        <svg
          viewBox={`0 0 ${L.width} ${L.height}`}
          role="img"
          aria-label="Galton board animation: agent dots falling into bull, base, and bear bins"
          style={{ width: "100%", display: "block" }}
        >
          <text
            x={L.dropX}
            y={22}
            textAnchor="middle"
            style={{
              fontFamily: MONO,
              fontSize: 10,
              fill: "var(--color-gold, #F59E0B)",
              letterSpacing: 1.5,
              opacity: consensusVisible ? 1 : 0,
              transition: `opacity .6s ${EASE}`,
            }}
          >
            {verdictText}
          </text>

          <g aria-hidden>
            {pegs.map((p, i) => (
              <circle key={i} cx={p.x} cy={p.y} r={2} fill="rgba(148,163,184,.3)" />
            ))}
          </g>

          {/* bins */}
          <g aria-hidden>
            <line x1={180} y1={225} x2={180} y2={292} stroke="rgba(148,163,184,.25)" />
            <line x1={280} y1={225} x2={280} y2={292} stroke="rgba(148,163,184,.25)" />
            <line x1={60} y1={292} x2={400} y2={292} stroke="rgba(148,163,184,.35)" />
            {[0, 1, 2].map((b) => (
              <text
                key={b}
                x={L.binCenters[b]}
                y={305}
                textAnchor="middle"
                fill={BIN_FILL[b]}
                style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 1.5 }}
              >
                {GALTON_BIN_LABELS[b]}
              </text>
            ))}
            {[0, 1, 2].map((b) => (
              <text
                key={b}
                ref={(el) => {
                  countRefs.current[b] = el;
                }}
                x={L.binCenters[b]}
                y={218}
                textAnchor="middle"
                fill={BIN_FILL[b]}
                style={{ fontFamily: MONO, fontSize: 11 }}
              >
                {/* animated mode: onFrame owns this text (constant vdom child
                    means React never clobbers the imperative writes) */}
                {animated ? 0 : finalCounts[b]}
              </text>
            ))}
          </g>

          {/* the 80 agent dots */}
          <g aria-hidden>
            {run.balls.map((ball, i) => {
              const final = positionAt(ball, 1);
              return (
                <circle
                  key={i}
                  ref={(el) => {
                    dotRefs.current[i] = el;
                  }}
                  r={L.dotRadius}
                  cx={animated ? L.dropX : final.x}
                  cy={animated ? L.dropY : final.y}
                  opacity={animated ? 0 : 1}
                  fill={animated ? FALL_FILL : BIN_FILL[ball.bin]}
                />
              );
            })}
          </g>
        </svg>

        {/* side panel */}
        <div className="wtm-glt-side" style={{ padding: 20, fontFamily: MONO }}>
          <div style={labelStyle}>Dropped</div>
          <div
            ref={droppedRef}
            style={{ fontSize: 22, fontFamily: SERIF, margin: "4px 0 16px", color: "var(--color-text, #F1F5F9)" }}
          >
            {animated ? `0 / ${run.total}` : `${run.total} / ${run.total}`}
          </div>

          <div style={labelStyle}>Live split</div>
          <div
            ref={splitRef}
            style={{ fontSize: 22, fontFamily: SERIF, margin: "4px 0 16px", color: "var(--color-text, #F1F5F9)" }}
          >
            {animated ? "- / - / -" : finalSplit.map((p) => `${p}%`).join(" / ")}
          </div>

          {/* consensus card — appears once every dot has settled */}
          <div
            style={{
              border: "1px solid rgba(245,158,11,.55)",
              borderRadius: 10,
              padding: "12px 14px",
              background: "rgba(15,23,41,.6)",
              opacity: consensusVisible ? 1 : 0,
              transform: consensusVisible ? "none" : "translateY(6px)",
              transition: `opacity .6s ${EASE}, transform .6s ${EASE}`,
            }}
          >
            <div style={{ ...labelStyle, color: "var(--color-gold, #F59E0B)" }}>Consensus</div>
            <div
              style={{
                fontFamily: SERIF,
                fontSize: 16,
                color: "var(--color-text, #F1F5F9)",
                margin: "6px 0 10px",
              }}
            >
              {GALTON_BIN_LABELS[lead]} case leads · {finalSplit[lead]}%
            </div>
            {scenarioRows.map((row) => (
              <div
                key={row.label}
                style={{
                  display: "grid",
                  gridTemplateColumns: "38px 1fr 32px",
                  gap: 8,
                  alignItems: "center",
                  fontSize: 10,
                  color: "var(--color-muted, #94A3B8)",
                  padding: "3px 0",
                }}
              >
                <span>{row.label}</span>
                <span
                  style={{
                    height: 5,
                    background: "rgba(148,163,184,.12)",
                    borderRadius: 3,
                    overflow: "hidden",
                  }}
                >
                  <span
                    style={{
                      display: "block",
                      height: "100%",
                      borderRadius: 3,
                      background: row.fill,
                      width: consensusVisible ? `${row.pct}%` : 0,
                      transition: `width .9s ${EASE}`,
                    }}
                  />
                </span>
                <span style={{ textAlign: "right" }}>{row.pct}%</span>
              </div>
            ))}
          </div>

          <div
            style={{
              fontSize: 10,
              color: "var(--color-dim, #7E8FA6)",
              lineHeight: 1.7,
              borderTop: `1px solid ${LINE}`,
              paddingTop: 14,
              marginTop: 16,
            }}
          >
            Each dot is one agent&apos;s final stance after structured debate. The
            distribution is the verdict: a tight pile means consensus, a flat
            spread means the panel genuinely disagrees, and that disagreement
            is the finding.
          </div>
        </div>
      </div>

      {/* disclaimer */}
      <div
        style={{
          borderTop: `1px solid ${LINE}`,
          padding: "9px 20px",
          fontFamily: MONO,
          fontSize: 10,
          letterSpacing: "0.08em",
          color: "var(--color-dim, #7E8FA6)",
        }}
      >
        EXPERIMENTAL AI SIMULATION · ILLUSTRATIVE DISTRIBUTION · NOT INVESTMENT ADVICE
      </div>
    </section>
  );
}
